import React, {useEffect} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import ModalWindow from '../ModalWindow/ModalWindow';
import ModalButton from '../ModalWindow/ModalButton/ModalButton';
import {
  selectCurrentPlayer,
  selectHexes,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPhase,
  selectSpecialBuildings,
  selectUnits,
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {
  selectRepairBuildingHexKey,
  selectRepairBuildingPlayerId,
  selectShowRepairBuildingModal
} from '../../../redux/reducers/privateStateReducer/selectors';
import {PHASES} from '../../../constants/phases';
import {hideRepairBuildingModal, repairBuilding, showToast} from '../../../redux/actions';
import './RepairBuildingModal.css';
import {WAR_COLOR} from "../../../styles/constants";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {
  checkIfHasRemainingConstructionActions
} from "../../../utils/gameState/construction/checkIfHasRemainingConstructionActions";
import {
  TEXT_CANNOT_REPAIR_WHEN_ADJACENT_ENEMY_UNIT,
  TEXT_NO_ACTIONS_REMAINING,
  TEXT_TURN_ALREADY_ENDED
} from "../../../constants/text/construction";
import {getNeighborEnemyUnits} from "../../../utils/unit/getNeighborEnemyUnits";
import {getTokenConfig} from "../../../utils/buildings/getTokenConfig";
import {checkTutorialActionWouldApply} from "../../../utils/tutorial/tutorialActionGate";
import {checkIfCentralQuarter} from "../../../utils/buildings/checkIfCentralQuarter";
import CentralQuarterIcon from "../../TokensToolbar/CentralQuarterIcon/CentralQuarterIcon";

const RepairBuildingModal = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const showModal = useSelector(selectShowRepairBuildingModal);
  const repairHexKey = useSelector(selectRepairBuildingHexKey);
  const hexes = useSelector(selectHexes);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const units = useSelector(selectUnits);
  const pendingBuildings = useSelector(selectPendingBuildings);
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  const isInitiativeInterceptedWithIsolationism = useSelector(selectIsInitiativeInterceptedWithIsolationism);
  // Ход и фаза перепроверяются, пока модалка открыта: клик по полю, который ее
  // открывает, закрыт гардом хода (GameScene), а сама модалка переживала смену
  // ходящего игрока — ремонт, нажатый в момент серверного завершения хода по
  // таймеру, уходил в pendingBuildings уже СЛЕДУЮЩЕМУ игроку (редьюсер
  // REPAIR_BUILDING берет игрока по currentPlayerIndex на момент применения).
  // Сверяется именно игрок, ОТКРЫВШИЙ модалку, а не "мой ли ход": в локальной
  // партии за одним устройством ходят все игроки по очереди, и там
  // checkIfCurrentUserTurn всегда истинен — ремонт так же уезжал бы соседу
  const repairBuildingPlayerId = useSelector(selectRepairBuildingPlayerId);
  const phase = useSelector(selectPhase);
  const isRepairAllowedNow = phase === PHASES.CONSTRUCTION
    && Boolean(repairBuildingPlayerId)
    && currentPlayer?.id === repairBuildingPlayerId;

  // Ход сменился, пока модалка открыта — закрываем ее, как гаснут остальные
  // элементы управления чужого хода
  useEffect(() => {
    if (showModal && !isRepairAllowedNow) {
      dispatch(hideRepairBuildingModal());
    }
  }, [showModal, isRepairAllowedNow, dispatch]);

  if (!showModal || !repairHexKey) {
    return null;
  }

  const handleClose = () => {
    dispatch(hideRepairBuildingModal());
  };

  // Определяем токен для отображения: спецстроение приоритетнее
  const tokenType = specialBuildings[repairHexKey] || hexes[repairHexKey];
  const buildingConfig = getTokenConfig(tokenType);
  const tokenImage = buildingConfig ? buildingConfig.image : null;
  const tokenLabel = buildingConfig ? buildingConfig.label : 'Строение';

  const handleRepair = () => {
    // Гонка «нажал ремонт в момент завершения хода»: эффект выше закроет
    // модалку следующим рендером, но клик мог случиться раньше него
    if (!isRepairAllowedNow) {
      dispatch(showToast(TEXT_TURN_ALREADY_ENDED, 'warning', 5000));
      dispatch(hideRepairBuildingModal());
      return;
    }

    let errorMessage;
    if (!checkIfHasRemainingConstructionActions(currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism)) {
      errorMessage = TEXT_NO_ACTIONS_REMAINING;
    } else if (getNeighborEnemyUnits(currentPlayer.id, repairHexKey, units).length > 0) {
      errorMessage = TEXT_CANNOT_REPAIR_WHEN_ADJACENT_ENEMY_UNIT;
    }

    if (errorMessage) {
      dispatch(showToast(errorMessage, 'warning', 5000));
      return;
    }

    // Гейт обучения: заблокированный ремонт не должен звучать и закрывать
    // модалку как успех — диспатчим (гейт покажет подсказку) и выходим
    const repairAction = repairBuilding(repairHexKey, 1, tokenLabel);
    if (!checkTutorialActionWouldApply(store.getState(), repairAction)) {
      dispatch(repairAction);
      return;
    }

    dispatch(repairAction);
    dispatch(hideRepairBuildingModal());
    REGISTRY_SINGLETON.getSoundManager().playBuildingRepair();
  };

  return (
    <ModalWindow handleClose={handleClose} className={'building-repair-modal'}>
      <div className="repair-building-modal-header">
        <h2>Поврежденное строение</h2>
      </div>

      <div className="repair-building-modal-content">
        {/* У центрального квартала нет готовой картинки под цвет игрока (пути
            image в его конфигах ведут в несуществующую папку) — он собирается
            из базового квартала и флага цвета владельца, как в панели строений
            и в инспекции ячейки */}
        {checkIfCentralQuarter(tokenType) ? (
          <CentralQuarterIcon token={tokenType} className={'repair-building-modal-central-quarter'}/>
        ) : (
          <img
            className="repair-building-modal-token"
            src={tokenImage}
            alt={tokenLabel}
          />
        )}
      </div>

      <div className="repair-building-modal-footer">
        <ModalButton
          onClick={handleRepair}
          className={'repair-building-modal-button'}
          noSound={true}
        >
          <h2>Починить</h2>
          <img
            className="repair-building-modal-action-img"
            src={`/img/icons/building_damage.png`}
            alt={'damage'}
          />
          <h2>за</h2>
          <img
            className="repair-building-modal-action-img"
            src={`/img/icons/action.png`}
            alt={'Действие'}
            title={'Действие в Фазе Строительства'}
          />
        </ModalButton>
        <ModalButton
          onClick={handleClose}
          style={{
            background: WAR_COLOR,
          }}
          text={'Отмена'}
          className={'repair-building-modal-button'}
        />
      </div>
    </ModalWindow>
  );
};

export default RepairBuildingModal;
